import { type FC, useLayoutEffect } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { NavItem } from './NavItem.tsx';
import { NavExpandingAreaItems } from './NavExpandingAreaItems.tsx';
import { trackNavInteraction } from '../../helpers/tracking.ts';
import { isMobile, isMobileOrTablet } from 'dibs-media-query';
import { useNavContext } from '../../NavContext.tsx';
import { type NavItems_seller$key } from './__generated__/NavItems_seller.graphql.ts';
import { type NavItems_links$key } from './__generated__/NavItems_links.graphql.ts';
import { type NavItems_notifications$key } from './__generated__/NavItems_notifications.graphql.ts';
import { type NavItems_viewer$key } from './__generated__/NavItems_viewer.graphql.ts';

import styles from './styles/NavItems.module.css';

type Props = {
    activeLink: string;
    seller?: NavItems_seller$key | null;
    links: NavItems_links$key;
    notifications?: NavItems_notifications$key | null;
    viewer?: NavItems_viewer$key | null;
};

const sellerFragment = graphql`
    fragment NavItems_seller on Seller {
        ...NavItem_seller
        ...NavExpandingAreaItems_seller
    }
`;

const linksFragment = graphql`
    fragment NavItems_links on NavBarLinks {
        navItems {
            ...NavItem_navItem
            ...NavExpandingAreaItems_navItem
            ... on NavBarList {
                items {
                    title
                    href
                }
            }
            title
            href
            dataTn
            newWindow
            eventName
            interactionType
        }
    }
`;

const notificationsFragment = graphql`
    fragment NavItems_notifications on DealerNavBarBadges {
        ...NavItem_notifications
        ...NavExpandingAreaItems_notifications
    }
`;

const viewerFragment = graphql`
    fragment NavItems_viewer on Viewer {
        ...NavItem_viewer
        ...NavExpandingAreaItems_viewer
    }
`;

export const NavItems: FC<Props> = ({
    activeLink,
    seller: sellerRef,
    links: linksRef,
    notifications: notificationsRef,
    viewer: viewerRef,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);
    const links = useFragment(linksFragment, linksRef);
    const notifications = useFragment(notificationsFragment, notificationsRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const { status, setExpandedArea, setActiveExpandedArea } = useNavContext();
    const isMobileView = isMobile();
    const isMobileOrTabletView = isMobileOrTablet();

    const { navItems } = links;

    useLayoutEffect(() => {
        const activeExpandingArea = navItems?.find(
            navItem => navItem?.items?.some(i => i?.href === activeLink) || false
        );
        if (activeExpandingArea) {
            if (status === 'expanded' || (isMobileOrTabletView && status === 'overlay')) {
                setExpandedArea(activeExpandingArea.eventName);
            }
            setActiveExpandedArea(activeExpandingArea.eventName);
        }
    }, [
        activeLink,
        navItems,
        status,
        isMobileOrTabletView,
        setActiveExpandedArea,
        setExpandedArea,
    ]);

    return (
        <>
            {navItems?.map(navItem => {
                if (
                    !navItem ||
                    navItem.dataTn === 'support' ||
                    (isMobileView && status === 'collapsed')
                ) {
                    return null;
                }
                const { items, title, href, eventName, interactionType, newWindow, dataTn } =
                    navItem;
                const hasSubItems = items?.length;

                return hasSubItems ? (
                    <NavExpandingAreaItems
                        key={title}
                        seller={seller}
                        navItem={navItem}
                        notifications={notifications}
                        activeLink={activeLink}
                        viewer={viewer}
                    />
                ) : (
                    <Link
                        key={title}
                        href={href || ''}
                        dataTn={dataTn || undefined}
                        target={newWindow ? '_blank' : undefined}
                        onClick={() => {
                            if (interactionType) {
                                trackNavInteraction({
                                    action: eventName,
                                    label: eventName,
                                    interactionType,
                                });
                            }
                            setExpandedArea(eventName);
                        }}
                        className={styles.link}
                    >
                        <NavItem
                            seller={seller}
                            navItem={navItem}
                            notifications={notifications}
                            isActive={activeLink === href}
                            viewer={viewer}
                        />
                    </Link>
                );
            })}
        </>
    );
};
